import { PhotoRecord } from "@/lib/catalog"
import { extension } from "@/storage/file-system"
import { Sentry } from "@/telemetry/sentry"
import { isAbortError, isNotFoundError, isUnsupportedFileError } from "@/utils/error"
import { getSupportEmail } from "@/utils/support-email"
import toast from "react-hot-toast"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n
import i18n from "@/i18n"
import { getFileForPhoto } from "../files-state"
import { getFileInfo } from "@/utils/file"
import { getPhotoMetadata } from "./photos-state"
import { analytics } from "@/telemetry/amplitude"

export const showPhotoError = (photoOrError: PhotoRecord | Error) => {
	const error = photoOrError instanceof Error ? photoOrError : photoOrError.error
	if (!error) {
		return
	}

	if (isAbortError(error)) {
		return
	} else if (isNotFoundError(error)) {
		toast.error(i18n.t("store:photoError.photoMissingError"))
	} else if (isUnsupportedFileError(error)) {
		showUnsupportedError()
	} else {
		console.error(error)
		toast.error(i18n.t("store:photoError.photoLoadingError"))
	}
}

export const showUnsupportedError = () => {
	const supportEmail = getSupportEmail()
	toast.error(i18n.t("store:photoError.photoUnsupportedError", { email: supportEmail }), {
		duration: 3000,
	})
}

export const logPhotoError = async (
	error: any,
	options: {
		photoId: string
		source: string
		logAnalytics?: boolean
		logStorage?: boolean
	},
) => {
	const file = getFileForPhoto(options.photoId)
	const metadata = getPhotoMetadata(options.photoId)

	const getSizeRange = (size: number) => {
		const sizeInMB = size / 1024 / 1024
		return sizeInMB === 0 ? "0" : sizeInMB < 50 ? "<50" : sizeInMB < 100 ? "<100" : ">100"
	}

	let tags: Record<string, string> = { id: options.photoId, source: options.source }

	if (!file) {
		const { name, size } = getFileInfo(options.photoId)
		tags = { ...tags, size: getSizeRange(size), format: extension(name ?? "") ?? "unknown" }
	} else {
		tags = { ...tags, size: getSizeRange(file.size), format: extension(file) ?? "unknown" }
	}

	if (metadata) {
		tags = {
			...tags,
			make: metadata.cameraMake ?? "unknown",
			model: metadata.cameraModel ?? "unknown",
		}
	}

	if (options.logStorage) {
		const estimate = await navigator.storage.estimate()
		tags = {
			...tags,
			quota: estimate.quota?.toString() ?? "unknown",
			usage: estimate.usage?.toString() ?? "unknown",
			remaining:
				estimate.quota && estimate.usage
					? (estimate.quota - estimate.usage).toString()
					: "unknown",
		}
	}

	// won't log NotFoundError
	if (!isNotFoundError(error)) {
		Sentry.captureException(error, {
			tags,
			level: options.logStorage ? "warning" : "error",
		})
	}

	if (options.logAnalytics) {
		analytics.trackEvent({
			label: "photo_error",
			customProperties: tags,
			type: "error",
			errorReason: error.message,
		})
	}
}
